export abstract class BrowserStorageService {
    private static readonly officeHomeStorageKeyPrefix = 'officeHome';
    private static readonly unknownUserId = 'Unknown';
    private readonly nativeStorage: Storage;
    private readonly userId: string;

    constructor(
        userId: string,
        storage: Storage
    ) {
        this.userId = userId || BrowserStorageService.unknownUserId;
        this.nativeStorage = storage;

        // When Safari is in private browsing mode .setItem throws an exception
        if (this.nativeStorage) {
            const key = this.getUserBasedKey(`_${Math.round(Math.random() * 1e6)}`);

            try {
                this.nativeStorage.setItem(key, key);
                this.nativeStorage.removeItem(key);
            } catch (err) {
                this.nativeStorage = null;
            }
        }
    }

    /**
     * Retrieve from `localStorage` (UserID-keyed)
     * @param key Key used to lookup in `localStorage`.
     * @returns the JSON-parsed value in `localStorage` for `key` (keyed by UserID)
     */
    public get(key: string): any {
        return this.getItem(this.getUserBasedKey(key));
    }

    /**
     * Store `value` in `localStorage` with `key` as the key, which is then UserID-keyed
     * @param key Key used to lookup in `localStorage`.
     * @param value Item to serialize to string and store in `localStorage`
     */
    public set(key: string, value: any): void {
        this.setItem(this.getUserBasedKey(key), value);
    }

    /**
     * Remove `value` in `localStorage` with `key` as the key, which is then UserID-keyed
     * @param key Key used to lookup in `localStorage`.
     */
    public remove(key: string): void {
        this.removeItem(this.getUserBasedKey(key));
    }

    /**
     * Get a non-UserID-keyed value from `localStorage` with `key` as the key.
     * @param key Key used to lookup in `localStorage`.
     * @returns the JSON-parsed value in `localStorage` for `key`
     */
    public getGlobal(key: string): any {
        return this.getItem(key);
    }

    /**
     * Store `value` in `localStorage` with `key` as the key.
     * @param key Key used to lookup in `localStorage`.
     * @param value Item to serialize to string and store in `localStorage`
     */
    public setGlobal(key: string, value: any): void {
        this.setItem(key, value);
    }

    /**
     * Remove `value` in `localStorage` with `key` as the key.
     * @param key Key used to lookup in `localStorage`.
     */
    public removeGlobal(key: string): void {
        this.removeItem(key);
    }

    public findKeys(substring: string): ReadonlyArray<string> {
        if (!this.nativeStorage || !substring) {
            return null;
        }

        const keys = [];
        for (let i = 0; i < this.nativeStorage.length; i++) {
            const key = this.nativeStorage.key(i);
            if (key.indexOf(substring) >= 0) {
                keys.push(key);
            }
        }

        return keys;
    }

    private getItem(key: string): any {
        if (!this.nativeStorage) {
            return null;
        }

        const value = this.nativeStorage.getItem(key);
        return typeof value === 'string' ? JSON.parse(value) : undefined;
    }

    private setItem(key: string, value: any): void {
        if (!this.nativeStorage) {
            return;
        }

        try {
            const jsonValue = typeof value === 'undefined' ? undefined : JSON.stringify(value);
            this.nativeStorage.setItem(key, jsonValue);
        } catch { /* Do nothing */ }
    }

    private removeItem(key: string): void {
        if (!this.nativeStorage) {
            return;
        }

        this.nativeStorage.removeItem(key);
    }

    private getUserBasedKey(key: string) {
        return `${BrowserStorageService.officeHomeStorageKeyPrefix}-${this.userId}${key}`;
    }
}
